import React, { useEffect, useMemo } from "react";
import MdaeDrawer from "../common_widgets/drawer/component";
import { useDispatch, useSelector } from "react-redux";
import { TOGGLE_VIEW_CARAC_MAPPINGS_PANEL } from "./reducers/view-carc-mappings-reducer";
import "./index.scss";
import MdaeDataGrid from "../common_widgets/data-grid/component";
import { IMdaeDataGridProps } from "../common_widgets/data-grid";
import { GET_ADJUSTMENT_CODES } from "../../pages/custom-CARC-mapping/services/get-adjustment-codes";
import { actions } from "async-ops";
import { formatAiSummaryText } from "./common-functions";


interface IViewCARCMappingsProps {
  children?: React.ReactNode;
  isFromROISummary?: boolean;
}
interface IViewCARCMappingsPanelComponentProps {
  isFromROISummary?: boolean;
}
const ViewCARCMappingsPanelComponent: React.FC<IViewCARCMappingsPanelComponentProps> = ({
  isFromROISummary,
}) => {
  const dispatch = useDispatch();

  const adjustmentCodesList = useSelector(
    (state: any) => state.carcMappingReducer.adjustmentCodesList
  );

  useEffect(() => {
    if (!isFromROISummary) {
      dispatch(actions.asyncOperationStart(GET_ADJUSTMENT_CODES, {}));
    }
  }, []);

  const carcMappingsDataGridConfigs = useMemo(() => {
    return getCARCMappingsDataGridConfigs(adjustmentCodesList);
  }, [adjustmentCodesList]);

  const aiSummary = useSelector(
    (state: any) => state.aiSummaryReducer.aiSummary
  );

  return (
    <div
      className={
        isFromROISummary ? "view-ai-summary-panel" : "view-carc-mappings-panel"
      }
    >
      {isFromROISummary ? (
        <div className="ai-summary-scroll-wrapper">
          <div className="ai-summary-header">
            <div className="ai-summary-label">AI Summary</div>
            <div className="last-update-label">
              {aiSummary?.createdDate
                ? `Last Updated: ${new Date(
                    aiSummary.createdDate
                  ).toLocaleDateString()}`
                : null}
            </div>
          </div>

          {!aiSummary?.summaryText ? (
            <div className="ai-summary-generating-message">
               <div className="ai-summary-loader">
                <div className="spinner" />
              </div>              
              AI Summary is being generated, please wait for it to
              complete.
            </div>
          ) : (
            <div
              className="ai-summary-text"
              dangerouslySetInnerHTML={{
                __html: formatAiSummaryText(aiSummary?.summaryText),
              }}
            />
          )}
        </div>
      ) : (
        <div className="carc-mapping-datagrid">
          <MdaeDataGrid {...carcMappingsDataGridConfigs} />
        </div>
      )}
    </div>
  );
};

const getCARCMappingsDataGridConfigs = (adjustmentCodesList) => {
  const carcMappingsDataGridOption: IMdaeDataGridProps = {
    id: "pnlCARCMappingsList",
    filterRow: {
      visible: true,
    },
    keyExpr: "adjustmentReasonCode",
    width: "100%",
    height: "100%",
    dataSource: adjustmentCodesList,
    paging: { enabled: false },
    allowColumnResizing: true,
    allowColumnReordering: true,
    showBorders: true,
    searchPanel: {
      visible: true,
      placeholder: "Enter any CARC Code or Keyword...",
    },
    mdaeColumns: [
      {
        caption: "Code",
        dataField: "adjustmentReasonCode",
        width: "15%",
        allowFiltering: true,
      },
      {
        caption: "Name",
        dataField: "adjustmentReasonDesc",
        width: "40%",
      },
      {
        caption: "Adjustment Category",
        dataField: "adjustmentCategoryDesc",
        width: "25%",
        cellRender: (cellElement) => {
          return (
            <div>
              {cellElement?.data?.adjustmentCategoryDesc === null
                ? "Unmapped"
                : cellElement?.data?.adjustmentCategoryDesc}
            </div>
          );
        },
      },
      {
        caption: "Adj. Status",
        dataField: "adjustmentStatusDesc",
        width: "20%",
      },
    ],
  };
  return carcMappingsDataGridOption;
};

export const toggleViewCarcMappingsPanelOpen = (
  isViewCARCMappingPanelOpen,
  dispatch
) => {
  dispatch({
    type: TOGGLE_VIEW_CARAC_MAPPINGS_PANEL,
    data: {
      isViewCARCMappingPanelOpen: !isViewCARCMappingPanelOpen,
    },
  });
};
const ViewCARCMappingsPanel: React.FC<IViewCARCMappingsProps> = (props) => {
  const { isFromROISummary } = props;
  return (
    <MdaeDrawer
      id="pnlViewCARCMappings"
      openedStateMode={"overlap"}
      position={"right"}
      revealMode={"slide"}
      component={() => (
        <ViewCARCMappingsPanelComponent isFromROISummary={isFromROISummary} />
      )}
      mdaeDataUpdaterConfig={[
        {
          actionType: TOGGLE_VIEW_CARAC_MAPPINGS_PANEL,
          reducerName: "viewCARCMappingsReducer",
          widgetOptionBindings: {
            opened: "isViewCARCMappingPanelOpen",
          },
        },
      ]}
    >
      {props.children}
    </MdaeDrawer>
  );
};

export default ViewCARCMappingsPanel;
